import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router";
import { Plus, Search } from "lucide-react";
import { useOrgContext } from "../../../context/OrgContext";
import { useAuth } from "../../../context/AuthContext";
import { RefreshIconButton } from "../../../components/ui/RefreshButton";
import { useLocale } from "../../../context/LanguageContext";
import { toBcp47 } from "../../../i18n";
import { useImageUpload } from "../../marketplace/hooks/useImageUpload";
import {
  useOrganizationChallenges,
  useCreateChallenge,
  useUpdateChallenge,
  usePublishChallenge,
  useStopChallengeRecurrence,
} from "../hooks/useChallenges";
import { ChallengeCard } from "./ChallengeCard";
import { CreateChallengeDrawer } from "./CreateChallengeDrawer";
import { ChallengeTemplates } from "./ChallengeTemplates";
import { DraftSuccessView } from "./DraftSuccessView";
import { PublishConfirmDialog } from "./PublishConfirmDialog";
import { CancelConfirmDialog } from "./CancelConfirmDialog";
import { DeleteDraftDialog } from "./DeleteDraftDialog";
import { StopRecurrenceDialog } from "./StopRecurrenceDialog";
import {
  CHALLENGE_TYPE_LABEL_KEYS,
  CHALLENGE_METRIC_LABEL_KEYS,
  CHALLENGE_METRIC_UNIT_KEYS,
} from "../constants";
import type { Challenge, ChallengeStatus, ChallengeTemplate } from "../types";

// ─── Status Filter Tabs ──────────────────────────────────────────────────────

const STATUS_TABS: Array<{ labelKey: string; value: ChallengeStatus | null }> = [
  { labelKey: "challenges.statusAll", value: null },
  { labelKey: "challenges.statusDraft", value: "DRAFT" },
  { labelKey: "challenges.statusUpcoming", value: "UPCOMING" },
  { labelKey: "challenges.statusActive", value: "ACTIVE" },
  { labelKey: "challenges.statusCompleted", value: "COMPLETED" },
  { labelKey: "challenges.statusCancelled", value: "CANCELLED" },
];

const VALID_TAB_VALUES = new Set<string>(["DRAFT", "UPCOMING", "ACTIVE", "COMPLETED", "CANCELLED"]);
// ─── Error Helpers ─────────────────────────────────────────────────────────

const ERROR_MAP: Array<[RegExp, string]> = [
  [/start date must be before end date/i, "challenges.errors.startBeforeEnd"],
  [/start date.*past/i, "challenges.errors.startInPast"],
  [/name.*required/i, "challenges.errors.nameRequired"],
  [/already exists/i, "challenges.errors.nameExists"],
  [/not authorized/i, "challenges.errors.notAuthorized"],
  [/image.*upload/i, "challenges.errors.imageUpload"],
];

function getSubmitErrorKey(err: unknown): string {
  const raw =
    err instanceof Error ? err.message : String(err);

  for (const [pattern, key] of ERROR_MAP) {
    if (pattern.test(raw)) return key;
  }

  return "challenges.errors.generic";
}
// ─── Component ───────────────────────────────────────────────────────────────

export function ChallengesPage() {
  const { currentOrgId, currentOrg } = useOrgContext();
  const { user } = useAuth();
  const { t, locale } = useLocale();
  const isOrgAdmin = user?.role === "ORG_ADMIN";

  // ─── Search & Filter State ────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // ─── URL ↔ Tab Sync (deep-link support) ───────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab")?.toUpperCase();
  const initialTab: ChallengeStatus | null =
    tabParam && VALID_TAB_VALUES.has(tabParam) ? (tabParam as ChallengeStatus) : "ACTIVE";
  const [statusFilter, setStatusFilter] = useState<ChallengeStatus | null>(initialTab);

  const handleTabChange = useCallback((value: ChallengeStatus | null) => {
    setStatusFilter(value);
    setSearchParams(value ? { tab: value } : {}, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // ─── Data Fetching ────────────────────────────────────────────────────
  const { data, loading, refetch } = useOrganizationChallenges({
    organizationId: currentOrgId ?? "",
    limit: 100,
  });

  const allChallenges: Challenge[] =
    data?.organizationChallenges?.items ?? [];
  const totalCount = data?.organizationChallenges?.totalCount ?? 0;

  // Client-side filtering (backend returns all for the org)
  const filteredChallenges = allChallenges.filter((c) => {
    if (statusFilter && c.status !== statusFilter) return false;
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      return c.name.toLowerCase().includes(q);
    }
    return true;
  });

  // ─── Drawer State ─────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState<
    Challenge | undefined
  >();
  const [duplicateFrom, setDuplicateFrom] = useState<Challenge | undefined>();
  const [templateFill, setTemplateFill] = useState<ChallengeTemplate | null>(
    null,
  );

  // ─── Publish Dialog ───────────────────────────────────────────────────
  const [publishTarget, setPublishTarget] = useState<Challenge | null>(null);

  // ─── Cancel Dialog ────────────────────────────────────────────────────
  const [cancelTarget, setCancelTarget] = useState<Challenge | null>(null);

  // ─── Stop Recurrence Dialog ───────────────────────────────────────────
  const [stopRecurrenceTarget, setStopRecurrenceTarget] = useState<Challenge | null>(null);

  // ─── Delete Draft Dialog ──────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState<Challenge | null>(null);

  // ─── Draft Success State ──────────────────────────────────────────────
  const [createdDraft, setCreatedDraft] = useState<Challenge | null>(null);
  const [draftVariant, setDraftVariant] = useState<"created" | "updated">("created");

  // ─── Submit Error ──────────────────────────────────────────────────────
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ─── Mutations ────────────────────────────────────────────────────────
  const [createChallenge, { loading: creating }] = useCreateChallenge();
  const [updateChallenge, { loading: updating }] = useUpdateChallenge();
  const [publishChallenge, { loading: publishing }] = usePublishChallenge();
  const [stopRecurrence, { loading: stoppingRecurrence }] = useStopChallengeRecurrence();
  const { upload: uploadImage, uploading: uploadingImage } = useImageUpload({
    purpose: "CHALLENGE",
  });

  // ─── Handlers ─────────────────────────────────────────────────────────

  const openCreateDrawer = useCallback(() => {
    setEditingChallenge(undefined);
    setDuplicateFrom(undefined);
    setTemplateFill(null);
    setSubmitError(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((challenge: Challenge) => {
    setEditingChallenge(challenge);
    setDuplicateFrom(undefined);
    setTemplateFill(null);
    setSubmitError(null);
    setDrawerOpen(true);
  }, []);

  const handleDuplicate = useCallback((challenge: Challenge) => {
    setEditingChallenge(undefined);
    setDuplicateFrom(challenge);
    setTemplateFill(null);
    setSubmitError(null);
    setDrawerOpen(true);
  }, []);

  const handleTemplateSelect = useCallback((template: ChallengeTemplate) => {
    setEditingChallenge(undefined);
    setDuplicateFrom(undefined);
    setTemplateFill(template);
    setSubmitError(null);
    setDrawerOpen(true);
  }, []);

  const handlePublish = useCallback((challenge: Challenge) => {
    setPublishTarget(challenge);
  }, []);

  const handleSubmit = useCallback(
    async (input: Record<string, unknown>, imageFile?: File | null) => {
      setSubmitError(null);
      try {
        let imageUrl: string | undefined;

        if (imageFile) {
          const entityId =
            editingChallenge?._id ??
            Array.from(crypto.getRandomValues(new Uint8Array(12)))
              .map((b) => b.toString(16).padStart(2, "0"))
              .join("");
          imageUrl = await uploadImage(imageFile, entityId);
          input.photoUrl = imageUrl;
        }

        if (editingChallenge) {
          const { data: updateData } = await updateChallenge({
            variables: { id: editingChallenge._id, input },
          });
          console.info(
            "[ChallengesPage] Challenge updated:",
            editingChallenge._id,
          );
          const updated = updateData?.updateChallenge as Challenge | undefined;
          if (updated) {
            setDraftVariant("updated");
            setCreatedDraft(updated);
          } else {
            setDrawerOpen(false);
          }
          setEditingChallenge(undefined);
          setDuplicateFrom(undefined);
          setTemplateFill(null);
        } else {
          const { data: createData } = await createChallenge({ variables: { input } });
          console.info("[ChallengesPage] Challenge created");
          const newChallenge = createData?.createChallenge as Challenge | undefined;
          if (newChallenge) {
            setDraftVariant("created");
            setCreatedDraft(newChallenge);
          } else {
            setDrawerOpen(false);
          }
          setEditingChallenge(undefined);
          setDuplicateFrom(undefined);
          setTemplateFill(null);
        }

        refetch();
      } catch (err) {
        console.error("[ChallengesPage] Submit failed:", err);
        setSubmitError(t(getSubmitErrorKey(err)));
      }
    },
    [
      editingChallenge,
      uploadImage,
      updateChallenge,
      createChallenge,
      refetch,
    ],
  );

  const handleConfirmPublish = useCallback(async () => {
    if (!publishTarget) return;
    try {
      await publishChallenge({
        variables: { id: publishTarget._id },
      });
      console.info(
        "[ChallengesPage] Challenge published:",
        publishTarget._id,
      );
      setPublishTarget(null);
      refetch();
    } catch (err) {
      console.error("[ChallengesPage] Publish failed:", err);
    }
  }, [publishTarget, publishChallenge, refetch]);

  const handleCancel = useCallback((challenge: Challenge) => {
    setCancelTarget(challenge);
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelTarget) return;
    try {
      await updateChallenge({
        variables: {
          id: cancelTarget._id,
          input: { status: "CANCELLED" },
        },
      });
      console.info(
        "[ChallengesPage] Challenge cancelled:",
        cancelTarget._id,
      );
      setCancelTarget(null);
      refetch();
    } catch (err) {
      console.error("[ChallengesPage] Cancel failed:", err);
    }
  }, [cancelTarget, updateChallenge, refetch]);

  const handleStopRecurrence = useCallback((challenge: Challenge) => {
    setStopRecurrenceTarget(challenge);
  }, []);

  const handleConfirmStopRecurrence = useCallback(async () => {
    if (!stopRecurrenceTarget) return;
    try {
      await stopRecurrence({
        variables: { id: stopRecurrenceTarget._id },
      });
      console.info(
        "[ChallengesPage] Recurrence stopped:",
        stopRecurrenceTarget._id,
      );
      setStopRecurrenceTarget(null);
      refetch();
    } catch (err) {
      console.error("[ChallengesPage] Stop recurrence failed:", err);
    }
  }, [stopRecurrenceTarget, stopRecurrence, refetch]);

  // ─── Delete Draft Handlers ────────────────────────────────────────────

  const handleDeleteDraft = useCallback((challenge: Challenge) => {
    setDeleteTarget(challenge);
  }, []);

  const handleConfirmDeleteDraft = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await updateChallenge({
        variables: {
          id: deleteTarget._id,
          input: { status: "CANCELLED" },
        },
      });
      console.info(
        "[ChallengesPage] Draft deleted:",
        deleteTarget._id,
      );
      setDeleteTarget(null);
      setCreatedDraft(null);
      setDrawerOpen(false);
      refetch();
    } catch (err) {
      console.error("[ChallengesPage] Delete draft failed:", err);
    }
  }, [deleteTarget, updateChallenge, refetch]);

  // ─── Draft Success View Handlers ─────────────────────────────────────

  const handleSuccessPublish = useCallback(() => {
    if (!createdDraft) return;
    setCreatedDraft(null);
    setDrawerOpen(false);
    setPublishTarget(createdDraft);
  }, [createdDraft]);

  const handleSuccessEdit = useCallback(() => {
    if (!createdDraft) return;
    setCreatedDraft(null);
    setEditingChallenge(createdDraft as Challenge);
    // drawer stays open, now in edit mode
  }, [createdDraft]);

  const handleSuccessDelete = useCallback(() => {
    if (!createdDraft) return;
    setDeleteTarget(createdDraft);
  }, [createdDraft]);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setEditingChallenge(undefined);
    setDuplicateFrom(undefined);
    setTemplateFill(null);
    setCreatedDraft(null);
    setSubmitError(null);
  }, []);

  // ─── Build Success View ──────────────────────────────────────────────

  function formatDateLong(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString(toBcp47(locale), {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  const successView = createdDraft ? (() => {
    const detailRows = [
      { label: t("shared.draftSuccess.rowName"), value: createdDraft.name },
      { label: t("shared.draftSuccess.rowType"), value: t(CHALLENGE_TYPE_LABEL_KEYS[createdDraft.type]) },
      { label: t("shared.draftSuccess.rowMetric"), value: t(CHALLENGE_METRIC_LABEL_KEYS[createdDraft.metric]) },
      ...(createdDraft.type === "HIT_THE_TARGET" && createdDraft.targetValue
        ? [{ label: t("shared.draftSuccess.rowTarget"), value: `${createdDraft.targetValue.toLocaleString(toBcp47(locale))} ${t(CHALLENGE_METRIC_UNIT_KEYS[createdDraft.metric])}` }]
        : []),
      { label: t("shared.draftSuccess.rowStart"), value: formatDateLong(createdDraft.startDate) },
      { label: t("shared.draftSuccess.rowEnd"), value: formatDateLong(createdDraft.endDate) },
      { label: t("shared.draftSuccess.rowAccess"), value: t("shared.draftSuccess.valueOrgOnly") },
    ];

    const cards = [{ title: t("shared.draftSuccess.cardDetails"), rows: detailRows }];

    if (createdDraft.entryFee > 0) {
      // Entry Fee card (mirrors mobile app)
      cards.push({
        title: t("shared.draftSuccess.cardEntryFee"),
        rows: [
          { label: t("shared.draftSuccess.rowType"), value: t("shared.draftSuccess.valuePaidChallenge") },
          { label: t("shared.draftSuccess.rowFee"), value: `${createdDraft.entryFee.toLocaleString(toBcp47(locale))} Koins` },
        ],
      });

      // Prize Pool preview (est. 10 participants — same as mobile app)
      const SAMPLE_PARTICIPANTS = 10;
      const BURN_RATE = 0.2;
      const DISTRIBUTION = [
        { label: t("shared.draftSuccess.firstPlace"), pct: 0.5 },
        { label: t("shared.draftSuccess.secondPlace"), pct: 0.3 },
        { label: t("shared.draftSuccess.thirdPlace"), pct: 0.2 },
      ];
      const totalPool = createdDraft.entryFee * SAMPLE_PARTICIPANTS;
      const winnerPool = totalPool - Math.floor(totalPool * BURN_RATE);

      cards.push({
        title: t("shared.draftSuccess.cardPrizePool", { count: SAMPLE_PARTICIPANTS }),
        rows: DISTRIBUTION.map((d) => ({
          label: d.label,
          value: `${Math.floor(winnerPool * d.pct).toLocaleString(toBcp47(locale))} Koins`,
        })),
      });
    }

    if (createdDraft.rewards && createdDraft.rewards.length > 0) {
      cards.push({
        title: t("shared.draftSuccess.cardRewards"),
        rows: createdDraft.rewards.map((r) => ({
          label: r.fromRank === r.toRank
            ? t("shared.draftSuccess.rankSingle", { rank: r.fromRank })
            : t("shared.draftSuccess.rankRange", { from: r.fromRank, to: r.toRank }),
          value: r.description,
        })),
      });
    }

    return (
      <DraftSuccessView
        entityType="challenge"
        variant={draftVariant}
        name={createdDraft.name}
        photoUrl={createdDraft.photoUrl}
        cards={cards}
        description={createdDraft.description}
        onPublish={handleSuccessPublish}
        onEdit={handleSuccessEdit}
        onDelete={handleSuccessDelete}
        onClose={handleDrawerClose}
      />
    );
  })() : undefined;

  // ─── Guard: no org selected (ORG_ADMIN) ─────────────────────────────────
  if (isOrgAdmin && (!currentOrgId || !currentOrg)) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-lg font-medium text-foreground">
          {t("common.selectOrg")}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("common.selectOrgHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("challenges.title")}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {currentOrg
              ? t("challenges.descriptionOrg", { orgName: currentOrg.name })
              : t("challenges.descriptionAll")}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateDrawer}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          {t("challenges.createChallenge")}
        </button>
      </div>

      {/* Search + Status Filter */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("challenges.searchPlaceholder")}
            className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Status Tabs */}
        <div className="flex items-center gap-1 rounded-lg border border-border bg-muted/30 p-1">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.labelKey}
              type="button"
              onClick={() => handleTabChange(tab.value)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                statusFilter === tab.value
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>

        {/* Refresh */}
        <RefreshIconButton onRefresh={async () => { await refetch(); }} loading={loading} />
      </div>

      {/* Challenge Templates */}
      {!debouncedSearch && (
        <ChallengeTemplates onSelect={handleTemplateSelect} />
      )}

      {/* Results count */}
      <p className="text-xs text-muted-foreground">
        {t("challenges.showingCount", { shown: filteredChallenges.length, total: totalCount })}
      </p>

      {/* Challenge Grid */}
      {loading && allChallenges.length === 0 ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className="h-48 animate-pulse rounded-lg border border-border bg-muted/30"
            />
          ))}
        </div>
      ) : filteredChallenges.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
          <p className="text-sm font-medium text-foreground">
            {debouncedSearch || statusFilter
              ? t("challenges.noMatchTitle")
              : t("challenges.emptyTitle")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {debouncedSearch || statusFilter
              ? t("challenges.noMatchHint")
              : t("challenges.emptyHint")}
          </p>
          <button
            type="button"
            onClick={openCreateDrawer}
            className="mt-4 inline-flex h-8 items-center gap-2 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-3.5 w-3.5" />
            {t("challenges.createChallenge")}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredChallenges.map((challenge) => (
            <ChallengeCard
              key={challenge._id}
              challenge={challenge}
              onEdit={handleEdit}
              onDuplicate={handleDuplicate}
              onPublish={handlePublish}
              onCancel={handleCancel}
              onDelete={handleDeleteDraft}
              onStopRecurrence={handleStopRecurrence}
            />
          ))}
        </div>
      )}

      {/* Create / Edit Drawer */}
      <CreateChallengeDrawer
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        onSubmit={handleSubmit}
        loading={creating || updating || uploadingImage}
        challenge={editingChallenge}
        duplicateFrom={duplicateFrom}
        templateFill={templateFill}
        currentOrgId={currentOrgId}
        successView={successView}
        error={submitError}
      />

      {/* Publish Confirmation */}
      {publishTarget && (
        <PublishConfirmDialog
          isOpen={true}
          onClose={() => setPublishTarget(null)}
          onConfirm={handleConfirmPublish}
          loading={publishing}
          challengeName={publishTarget.name}
        />
      )}

      {/* Cancel Confirmation */}
      {cancelTarget && (
        <CancelConfirmDialog
          isOpen={true}
          onClose={() => setCancelTarget(null)}
          onConfirm={handleConfirmCancel}
          loading={updating}
          entityType="challenge"
          entityName={cancelTarget.name}
        />
      )}

      {/* Stop Recurrence Confirmation */}
      {stopRecurrenceTarget && (
        <StopRecurrenceDialog
          isOpen={true}
          onClose={() => setStopRecurrenceTarget(null)}
          onConfirm={handleConfirmStopRecurrence}
          loading={stoppingRecurrence}
          entityType="challenge"
          entityName={stopRecurrenceTarget.name}
        />
      )}

      {/* Delete Draft Confirmation */}
      {deleteTarget && (
        <DeleteDraftDialog
          isOpen={true}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleConfirmDeleteDraft}
          loading={updating}
          entityType="challenge"
          entityName={deleteTarget.name}
        />
      )}
    </div>
  );
}
